import React, { useEffect, useState } from "react";
import Modal from "@visiblethread/react-components/dist/components/Modal";
import Button, {
  buttonVariants,
} from "@visiblethread/react-components/dist/components/Button";
import {
  Alert,
  alertTypes,
  alertVariants,
} from "@visiblethread/react-components/dist/components/Alert";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import { modalTypes } from "constants/types";
import PropTypes from "prop-types";
import { addUsersToWorkspace } from "slices/systemAdmin/actions/systemAdmin";
import { addUsersToWorkspace as userAdminAddUsersToWorkspace } from "slices/userAdmin/actions/userAdmin";
import AddUserModalUser from "layout/systemAdmin/workspaces/modals/AddUserModalUser";
import { SYSTEM_ADMIN_USERS } from "constants/routes";
import { Link } from "react-router-dom";
import { sortObjectArrayComparator } from "utils";
import { USERNAME } from "constants/systemAdmin";

const workspaceSelector = (state) =>
  window.location.pathname.includes("/systemAdmin")
    ? state.systemAdmin.workspacesInfo.workspaces
    : state.userAdmin.workspaceInfo.workspaces;

const usersSelector = (state) =>
  window.location.pathname.includes("/systemAdmin")
    ? state.systemAdmin.usersInfo.users
    : state.userAdmin.usersInfo.users;

const AddUserModal = ({ workspaceId, currentLicensedUsers }) => {
  const dispatch = useDispatch();
  const workspaces = useSelector(workspaceSelector);
  const users = useSelector(usersSelector);
  const error = useSelector((state) => state.error?.errorMessage);
  const [search, setSearch] = useState("");
  const [userList, setUserList] = useState([]);
  const [checkedUsers, setCheckedUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);

  useEffect(() => {
    let uniqueUsers = [...users];
    // users in workspaceId
    const workspaceUsers = workspaces
      ?.find((s) => s.id === workspaceId)
      .users.map((u) => u.username);
    // filter users already in that workspace
    uniqueUsers = uniqueUsers.filter(
      (uu) => !workspaceUsers.includes(uu.username)
    );
    uniqueUsers = uniqueUsers.sort(sortObjectArrayComparator(true, USERNAME));
    setUserList(uniqueUsers);
  }, []);

  useEffect(() => {
    setFilteredUsers([...new Set(userList.map((u) => u.username))]);
  }, [userList]);

  useEffect(() => {
    const filtered = [...new Set(userList.map((u) => u.username))];
    setFilteredUsers(filtered.filter((u) => u.includes(search)));
  }, [search]);

  const handleCancel = () => {
    dispatch(hideModal(modalTypes.ADD_USER_TO_WORKSPACE));
  };

  const handleCheckChange = (user) => {
    let checked = checkedUsers;
    checked = checked.find((u) => u === user)
      ? checked.filter((u) => u !== user)
      : [...checked, user];
    setCheckedUsers(checked);
    setCheckedUsers(checked);
  };

  const handleAdd = () => {
    if (window.location.pathname.includes("/systemAdmin")) {
      dispatch(
        addUsersToWorkspace(checkedUsers, currentLicensedUsers, workspaceId)
      );
    } else {
      dispatch(
        userAdminAddUsersToWorkspace(checkedUsers, currentLicensedUsers)
      );
    }
  };

  const emptyUsers = () => (
    <div
      className="add__user__modal__users__container__no_users"
      data-testid="empty-users"
    >
      {search !== "" ? (
        <span>No users match the filter</span>
      ) : (
        <span>
          No users available. Please <Link to={SYSTEM_ADMIN_USERS}>ADD</Link>{" "}
          users.
        </span>
      )}
    </div>
  );

  const modalBody = () => (
    <div className="add__user__modal">
      <input
        className="add__user__modal__search"
        placeholder="Type to filter"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="add__user__modal__users">
        <p className="add__user__modal__users__title">
          <span>
            <b className="intermedium">Select users from the list</b>
          </span>
        </p>
        <p className="add__user__modal__users__subtitle">
          <span className="intermedium">
            Click checkbox to add multiple users
          </span>
        </p>
        <div className="add__user__modal__users__container">
          {filteredUsers.length
            ? filteredUsers.map((u) => (
                <AddUserModalUser
                  u={u}
                  checkedUsers={checkedUsers}
                  handleChange={handleCheckChange}
                  hasMultipleWorkspaces={
                    userList.find((user) => user.username === u).workspaces
                      ?.length > 1
                  }
                />
              ))
            : emptyUsers()}
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      data-test="addUserModal"
      className="fade-in"
      show
      onDismiss={handleCancel}
      backdrop="static"
      centered
    >
      <Modal.Header closeButton>
        <b>Add Member(s)</b>
      </Modal.Header>
      <Modal.Body>
        {error !== undefined ? (
          <Alert
            variant={alertVariants.warning}
            text={error}
            type={alertTypes.inline}
            hideCloseButton
          />
        ) : null}
        {modalBody()}
      </Modal.Body>
      <Modal.Footer>
        <Button
          dataTestId="saveButton"
          variant={buttonVariants.primary}
          onClick={() => {
            handleAdd();
          }}
          disabled={!checkedUsers.length}
          content="Add Member(s)"
        />
        <Button
          dataTestId="cancelButton"
          variant={buttonVariants.secondary}
          onClick={handleCancel}
          content="Cancel"
        />
      </Modal.Footer>
    </Modal>
  );
};

export default AddUserModal;

AddUserModal.propTypes = {
  workspaceId: PropTypes.number.isRequired,
  currentLicensedUsers: PropTypes.number.isRequired,
};
